'use client'
import { Suspense, use } from 'react';
//import { FatalitiesReport, getFatalitiesReport, SelectedFilter, toQuery } from "@/api/dashboard";
import { useState, useEffect, useRef, useCallback } from 'react'
import { Chart, GoogleChartControlOptions, GoogleChartOptions, ChartWrapperOptions } from "react-google-charts";
import { Page, Text, View, Document, StyleSheet, PDFDownloadLink, pdf, Image } from '@react-pdf/renderer';
import Filter, { SelectionAction, SelectionChangeEvent } from './Filter';
import Modal from './Modal';
import { saveAs } from "file-saver";
//import Link from "next/link";
import { DocumentPdf } from '../lib/DocumentPdf';
//import { useRouter, useSearchParams } from 'next/navigation'
import { FatalitiesReport, SelectedFilter, toQuery, getFatalitiesReport, FilterDefinition, getFilters } from '../api/dashboard';
import { useNavigate, useSearchParams } from 'react-router';
import { useIsVisible } from '../hooks/useIsVisible';
import { useFilterToast } from '../hooks/useFilterToast';
import { buildFilterSummary } from './filterSummary';
import FilterToastStack from './FilterToast';

// Create styles
const styles = StyleSheet.create({
    page: {
        flexDirection: 'row',
        backgroundColor: '#E4E4E4'
    },
    section: {
        margin: 10,
        padding: 10,
        flexGrow: 1
    }
});
interface DashboardProps {
    url: string
}
export default function Dashboard({ url }: DashboardProps) {
    const [data, setData] = useState<FatalitiesReport | null>(null);
    const [isLoading, setLoading] = useState(true);
    const [queryStringLoaded, setQueryStringLoaded] = useState(false);
    const [selected, setSelected] = useState<SelectedFilter[]>([]);
    const [filterDefinitions, setFilterDefinitions] = useState<FilterDefinition[] | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    // Igloo's header (.top) overlays the top of the viewport once the page is
    // scrolled -- 70px desktop, 50px mobile. The visibility test and the jump
    // target both measure it here, from one source, so they cannot disagree
    // about where the headline has to land to count as readable.
    const [headerHeight, setHeaderHeight] = useState(0);
    useEffect(() => {
        const measure = () => {
            const header = document.querySelector('.top');
            setHeaderHeight(header ? header.getBoundingClientRect().height : 0);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    // Watch the headline count, not the whole graph block. Scrolled down to the
    // filters, the bottom of the chart grid can still be on screen while the
    // count -- the thing a filter change actually updates -- is not, which is
    // precisely the case where the jump link needs to appear. threshold 1 means
    // the whole headline must be in view, and the inset discounts the strip
    // hidden behind the header.
    const { ref: graphRef, node: graphNode, isVisible: isGraphVisible } =
        useIsVisible<HTMLHeadingElement>({
            threshold: 1,
            rootMargin: `-${headerHeight}px 0px 0px 0px`
        });
    const { toasts, show: showToast, dismiss: dismissToast, pause: pauseToast, resume: resumeToast } = useFilterToast();
    // Set by handSelectionChange so the toast only fires for filter changes the
    // user actually made -- not the initial load, and not the query-string
    // hydration below, which also writes to `selected`.
    const pendingToastRef = useRef<SelectedFilter[] | null>(null);
    // Read inside the fetch callback, where the `filterDefinitions` captured by
    // the effect closure may be a render behind.
    const filterDefinitionsRef = useRef<FilterDefinition[] | null>(null);
    // Filters apply fast enough that responses can land out of order; only the
    // newest request is allowed to write state or announce a count.
    const requestIdRef = useRef(0);

    useEffect(() => {
        filterDefinitionsRef.current = filterDefinitions;
    }, [filterDefinitions]);

    const scrollToGraphs = useCallback(() => {
        if (!graphNode) {
            return;
        }
        // Land the headline just clear of the header band the observer discounts,
        // so arriving here reliably counts as "visible" and retires the link.
        const reduceMotion = typeof window.matchMedia === 'function'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({
            top: graphNode.getBoundingClientRect().top + window.scrollY - headerHeight - 16,
            behavior: reduceMotion ? 'auto' : 'smooth'
        });
    }, [graphNode, headerHeight]);

    const handSelectionChange = (event: SelectionChangeEvent) => {
        let selectedLocal = null;
        console.log("Selection Change", event);
        switch (event.action) {
            case SelectionAction.Add:
                //todo: prevent duplicates
                selectedLocal = [...selected, { name: event.name, value: "" }];


                break;
            case SelectionAction.Remove:
                selectedLocal = selected.filter(i => i.name !== event.name);

                break;
            case SelectionAction.Change:
                if (event.name.endsWith("Date") && event.value == '') {
                    //If Date is empty, remove from the list
                    selectedLocal = selected.filter(i => i.name !== event.name);
                } else {
                    selectedLocal = [...selected.filter(i => i.name !== event.name), { name: event.name, value: event.value }];
                }

                break;
        }
        if (selectedLocal != null) {
            // Adding an empty filter row cannot change the results, so don't
            // announce a count the user hasn't actually moved yet.
            pendingToastRef.current = event.action === SelectionAction.Add ? null : selectedLocal;
            setSelected(selectedLocal);
            navigate(`${url}?` + toQuery(selectedLocal));

            //router.push('/?' + toQuery(selectedLocal));

        }

    }
    useEffect(() => {
        const requestId = ++requestIdRef.current;
        getFatalitiesReport(selected)
            .then((data) => {
                // A later filter change already landed; this response is stale.
                if (requestId !== requestIdRef.current) {
                    return;
                }
                setData(data);
                const pending = pendingToastRef.current;
                pendingToastRef.current = null;
                if (pending) {
                    showToast(buildFilterSummary(pending, filterDefinitionsRef.current), data.total);
                }
                getFilters().then((filterDefinitions) => {
                    if (requestId !== requestIdRef.current) {
                        return;
                    }
                    setFilterDefinitions(filterDefinitions);
                    setLoading(false);
                });
            })
    }, [selected, showToast]);

    if (isLoading) return <p>Loading...</p>
    if (!data) return <p>No profile data</p>
    if (!queryStringLoaded) {
        if (selected == null || selected.length < 1) {
            let params = [] as SelectedFilter[];
            searchParams.forEach((value, key) => {
                if (!value) {
                    return;
                }
                params.push({ name: key, value });

            });
            setSelected(params);
        }
        setQueryStringLoaded(true);
    }
    const show = searchParams.get('show') == "true";
    const chartHeight = 300;
    const aspectRatio = undefined;
    let geoUri: string | null;
    let yearsUri: string | null;
    let disciplinesUri: string | null;
    let incidentTypesUri: string | null;
    const date = new Date(data.date);
    // Rendered through a portal, so it is safe to mount from either branch.
    const toastElement = (
        <FilterToastStack
            toasts={toasts}
            showJumpLink={!isGraphVisible}
            onJump={scrollToGraphs}
            onDismiss={dismissToast}
            onPause={pauseToast}
            onResume={resumeToast}
        />
    );
    if (data.total === 0) {
        return (
            <div>

                <Filter selected={selected} onSelectionChange={handSelectionChange} filterDefinitions={filterDefinitions}></Filter>
                <h1 ref={graphRef} className='h3 mt-8 mb-2'>Struck-By Fatalities: {data.total}</h1>
                <div className='mb-2'>No items match your query. Please try filtering again using less restrictive criteria.</div>
                {toastElement}
            </div>
        )

    }
    return (
        <>
            <div>
                <Filter selected={selected} onSelectionChange={handSelectionChange} filterDefinitions={filterDefinitions}></Filter>
                <h1 ref={graphRef} className='h3 mt-8 mb-2'>Struck-By Fatalities: {data.total}</h1>
                <div className="grid grid--compact md:grid-cols-2 gap-4">


                    <div>
                        <h4 className='text-center'>Fatalities By State</h4>
                        <Chart
                            chartType="GeoChart"
                            width="100%"
                            style={{ aspectRatio: aspectRatio, minHeight: chartHeight }}

                            data={data.states}


                            options={{
                                region: "US", // Africa 
                                colorAxis: { colors: ["white", "red"] },
                                backgroundColor: "#e5e5e5",
                                datalessRegionColor: "#f8bbd0",
                                defaultColor: "#f5f5f5",

                                chartArea: { left: 40, right: 40, top: 40, bottom: 40 },
                                resolution: "provinces",

                            }}
                        />
                    </div>
                    <div>
                        <h4 className='text-center'>Fatalities Per Year</h4>
                        <Chart
                            chartType="LineChart"
                            width="100%"
                            style={{ aspectRatio: aspectRatio, minHeight: chartHeight }}
                            data={data.years}
                            options={{
                                backgroundColor: "#e5e5e5",
                                legend: { position: 'none' },

                                chartArea: { left: 40, right: 40, top: 40, bottom: 40 },
                                is3D: true
                            }}
                        />
                    </div>
                    <div>
                        <h4 className='text-center'>Fatalities By Discipline</h4>
                        <Chart
                            chartType="PieChart"
                            width="100%"
                            style={{ aspectRatio: aspectRatio, backgroundColor: "#e5e5e5", minHeight: chartHeight }}
                            data={data.disciplines}
                            options={{
                                backgroundColor: "#e5e5e5",

                                chartArea: { left: 40, right: 40, top: 40, bottom: 40 },
                                is3D: true
                            }}
                        />
                    </div>
                    <div>
                        <h4 className='text-center'>Fatalities During Response Activities</h4>
                        <Chart
                            chartType="BarChart"
                            width="100%"
                            style={{ aspectRatio: aspectRatio, backgroundColor: "#e5e5e5", minHeight: chartHeight }}
                            data={data.incidentTypes}

                            options={{
                                backgroundColor: "#e5e5e5",
                                chartArea: { left: 170, right: 40, top: 40, bottom: 40 },
                                legend: { position: 'none' },

                                is3D: true


                            }}
                        />
                    </div>
                </div>
                <h6>Move your cursor over the state, line, pie wedge, or bar to display the associated number of fatalities.</h6>
                <p>Report generated: {date.toLocaleString()}</p>
                <div>
                    <button
                        onClick={async () => {
                            if (geoUri == null || yearsUri == null || disciplinesUri == null || incidentTypesUri == null) {
                                return;
                            }
                            const doc = <DocumentPdf geoUri={geoUri} yearsUri={yearsUri} disciplinesUri={disciplinesUri} incidentTypesUri={incidentTypesUri}
                                selected={selected} filterDefinitions={filterDefinitions} fatalities={data.total} />;
                            const asPdf = pdf(); // {} is important, throws without an argument
                            asPdf.updateContainer(doc);
                            const blob = await asPdf.toBlob();
                            saveAs(blob, 'StruckBy-Custom-Report.pdf');
                        }}
                        className="button"
                    >Download PDF of Displayed Data</button>
                </div>
                <div className="mt-4">
                    <h3 className='mb-2'>Custom Reports</h3>
                    <p>If your research question cannot be answered using the filters above, you may request a Custom Report. Custom Report requests are answered as time and resources allow.</p>
                    <button onClick={() => {
                        if (show) {
                            navigate(`${url}?${toQuery(selected)}show=false`);
                        } else {
                            navigate(`${url}?${toQuery(selected)}show=true`);
                        }
                    }} className="button">
                        Request Custom Report
                    </button>
                </div>
                {show && <Modal url={url} />}
            </div>


            {/* Below are the charts for the PDF. They have to be separate to keep a uniform size. The "{height: 0, overflow: "hidden"}" is the trick for getting them to render correctly. */}
            <div style={{ height: 0, overflow: "hidden" }}>
                <Chart
                    chartType="GeoChart"
                    width="1200px"
                    height="600px"

                    data={data.states}

                    chartEvents={[
                        {
                            eventName: "ready",
                            callback({ chartWrapper }) {
                                geoUri = chartWrapper?.getChart().getImageURI() as unknown as string;
                            },
                        }
                    ]}
                    options={{
                        region: "US", // Africa
                        colorAxis: { colors: ["white", "red"] },
                        backgroundColor: "#e5e5e5",
                        datalessRegionColor: "#f8bbd0",
                        defaultColor: "#f5f5f5",
                        resolution: "provinces",
                    }}
                />
                <Chart
                    chartType="LineChart"
                    width="1200px"
                    height="600px"
                    data={data.years}
                    chartEvents={[
                        {
                            eventName: "ready",
                            callback({ chartWrapper }) {
                                yearsUri = chartWrapper?.getChart().getImageURI() as unknown as string;
                            },
                        }
                    ]}
                    options={{
                        backgroundColor: "#e5e5e5",
                        legend: { position: 'none' },

                        chartArea: { left: 40, right: 40, top: 40, bottom: 40 },
                        is3D: true
                    }}
                />
                <Chart
                    chartType="PieChart"
                    width="1200px"
                    height="600px"
                    data={data.disciplines}
                    chartEvents={[
                        {
                            eventName: "ready",
                            callback({ chartWrapper }) {
                                disciplinesUri = chartWrapper?.getChart().getImageURI() as unknown as string;
                            },
                        }
                    ]}
                    options={{
                        backgroundColor: "#e5e5e5",
                    }}
                />
                <Chart
                    chartType="BarChart"
                    width="1200px"
                    height="600px"
                    data={data.incidentTypes}
                    chartEvents={[
                        {
                            eventName: "ready",
                            callback({ chartWrapper }) {
                                incidentTypesUri = chartWrapper?.getChart().getImageURI() as unknown as string;
                            },
                        }
                    ]}
                    options={{
                        backgroundColor: "#e5e5e5",
                    }}
                />
            </div>
            {toastElement}
        </>
    );
}